import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps the supporting lines under the buy-box price — the "you save" pill and
 * the native unit price — in step with the selected variant.
 *
 * Both values are derived server-side, so this only has to swap in the
 * re-rendered markup. It cannot be left to the section morph: an ordinary
 * product-page variant change morphs the `<variant-picker>` element alone
 * (`variant-picker.js`, `morphElementSelector` is undefined off combined
 * listings), and every other part of the buy box updates itself by listening
 * for this same event. Without this component the block keeps showing the
 * previously selected variant's unit price — visibly wrong on a product sold in
 * several sizes, which is exactly where unit pricing matters.
 *
 * @extends {Component}
 */
class DavittaPriceNote extends Component {
  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();
    // The quick-add modal renders this block inside a <dialog>, outside any section.
    // Held rather than re-queried, because by disconnect time the element is
    // already detached and `closest` would no longer find the listener's owner.
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
  }

  /**
   * Handles product select event by re-rendering the note for the new variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newNote =
          html.querySelector(`davitta-price-note[data-block-id="${this.dataset.blockId}"]`) ??
          html.querySelector('davitta-price-note');
        if (!newNote) return;

        morph(this, newNote, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[davitta-price-note] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('davitta-price-note')) {
  customElements.define('davitta-price-note', DavittaPriceNote);
}
